import { FormControl, FormLabel, Radio, Select, Stack } from '@chakra-ui/react'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import RuianAddressSelector from '@/components/RuianAddressSelector'
import AgentPerson from '@/containers/forms/person/AgentPerson'
import { useApp } from '@/hooks/useApp'
import type { Step3FormValues } from '../..'
import { useInsuranceParams } from '../../../../../../context/InsuranceParamsProvider'
import { useSelections } from '../../../../../../hooks'
import { useNameof } from '../../../../../../hooks/useNameof'
import { FormBody } from '../../../../../forms/FormBody'
import { FormRow } from '../../../../../forms/FormRow'
import ICOOptions from '../../../../../forms/person/ICOOptions'
import InsureeContact from '../../../../../forms/person/InsureeContact'
import InsureeName from '../../../../../forms/person/InsureeName'
import { PersonID } from '../../../../../forms/person/PersonID'
import PersonType from '../../../../../forms/person/PersonType'
import { RadioGroup } from '../../../../../forms/RadioGroup'

export type AdditionalInfoFormProps = {
  isDisabledChanges: boolean
}

export const AdditionalHolderInfoForm = ({ isDisabledChanges }: AdditionalInfoFormProps) => {
  const { product } = useApp()
  const { isDisabled } = useInsuranceParams()

  const { watch, register, getValues, setValue } = useFormContext<Step3FormValues>()

  const nameof = useNameof<Step3FormValues>()
  const personPrefix = nameof(x => x.policyHolder.holder)
  const pnameof = useNameof<PersonAdditionalData>(personPrefix)
  const showAgent = watch('policyHolder.holder.agent.required')

  const { data: dataTitles } = useSelections(SelectionType.TitleBefore)

  const holderSameAS = watch('policyHolder.holderSameAs')
  const pType = watch('policyHolder.holder.type')

  const sameAsOptions = [
    { label: 'Stejný s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    ...(product == Product.VehicleLeasingInsurance ? [{ label: 'Stejný s klientem', value: Selection.PersonSameAsClient }] : []),
    { label: 'Jiný', value: null },
  ]

  return (
    <>
      <FormBody>
        <FormControl>
          <FormLabel>Držitel/provozovatel vozidla</FormLabel>

          <RadioGroup
            name="policyHolder.holderSameAs"
            value={holderSameAS?.toString()}
            isDisabled={isDisabledChanges || isDisabled}
            onChange={(value) => setValue('policyHolder.holderSameAs', parseInt(value))}
          >
            <Stack direction="row" spacing={16}>
              {sameAsOptions?.map((item) => (
                <Radio key={item.value} value={item.value?.toString() ?? ''}>
                  {item.label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        {!holderSameAS &&
          <>
            <PersonType personPrefix={personPrefix} disabled={true} />

            {([Selection.Person, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <PersonID personPrefix={personPrefix} disabled={true} />
            }

            {([Selection.Company, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <ICOOptions personPrefix={personPrefix} disabled={true} />
            }

            {([Selection.Person, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <FormRow itemsCount={3}>
                <FormControl>
                  <FormLabel>Titul před jménem</FormLabel>

                  {(dataTitles?.data.length ?? 0) > 0 &&
                    <Select
                      {...register('policyHolder.holder.titleBefore')}
                      isDisabled={isDisabled}
                      defaultValue={getValues('policyHolder.holder.titleBefore') ?? ''}
                    >
                      <option value=''>Vyberte</option>
                      {dataTitles?.data.map((item) => (
                        <option value={item.value?.toString()} key={item.id}>{item.value}</option>
                      ))}
                    </Select>
                  }
                </FormControl>

                <InsureeName personPrefix={personPrefix} disabled={true} />
              </FormRow>
            }

            <RuianAddressSelector
              addressPath={pnameof((x) => x.address)}
              isDisabled={true}
              showOnlyLotNumber={false}
            />

            {showAgent &&
              <AgentPerson personPrefix={personPrefix} />
            }

            <InsureeContact personPrefix={personPrefix} isDisabled={isDisabled} />
          </>
        }

      </FormBody>
    </>
  )
}